import { Link } from 'react-router-dom';
import { useAuthStore } from '@/features/auth/store';
import { postListFetcher } from '@/shared/api/list';
import { formatCost, formatUnixDateTime } from '@/shared/lib/datetime';
import { GenericListPage } from '@/shared/ui/list/GenericListPage';
import { ToggleCell } from '@/shared/ui/list/ToggleCell';
import type { ListPageConfig, ListRow } from '@/shared/ui/list/types';
import { saveOrderCheckbox } from './api';

const LIST_KEY = 'parts-orders';
const CONFIRM_ROLES = ['admin', 'part-supervisor'];

interface PartOrder extends ListRow {
  id: number;
  create_time?: number;
  delivery_address?: string;
  customer_name?: string;
  user_id?: number;
  customer_phone?: string;
  status_name?: string;
  client_order_sum?: number;
  supplier_order_sum?: number;
  bonus_pay?: number;
  checkbox?: number | boolean;
}

const STATUS_OPTIONS = [
  { value: '1', label: 'Заказ создан' },
  { value: '2', label: 'Сборка заказа' },
  { value: '3', label: 'Доставка' },
  { value: '4', label: 'Готов к выдаче' },
  { value: '5', label: 'Выдан' },
  { value: '6', label: 'Закрыт' },
];

/** Only admins and part supervisors may change the order checkbox. */
const OrderCheckbox = ({ order }: { order: PartOrder }) => {
  const roles = useAuthStore((s) => s.profile.roles);
  const canEdit = CONFIRM_ROLES.some((role) => role in roles);

  return (
    <ToggleCell
      checked={Boolean(order.checkbox)}
      disabled={!canEdit}
      listKey={LIST_KEY}
      toggle={(next) => saveOrderCheckbox(order.id, next)}
    />
  );
};

const config: ListPageConfig<PartOrder> = {
  queryKey: LIST_KEY,
  title: 'Заказы',
  boxTitle: 'Список заказов',
  errorText: 'Ошибка загрузки заказов',
  fetchList: postListFetcher('/part/order/list/'),
  rows: ['data'],
  searchKeys: [
    { value: 'order_id', label: 'Поиск по нашему номеру заказа' },
    { value: 'customer_name', label: 'Поиск по имени пользователя, создавшего заказ' },
    { value: 'customer_phone', label: 'Поиск по телефону пользователя, создавшего заказ' },
  ],
  filters: [
    { name: 'status', label: 'Фильтр по статусу', kind: 'select', options: STATUS_OPTIONS },
    { name: 'create_data', label: 'Поиск по дате создания заказа', kind: 'datetime' },
  ],
  columns: [
    {
      header: 'ID заказа',
      sortField: 'order_id',
      render: (order) => <Link to={`/parts/parts/${order.id}`}>{order.id}</Link>,
    },
    { header: 'Дата', sortField: 'create_data', render: (order) => formatUnixDateTime(order.create_time) },
    { header: 'Пункт самовывоза', render: (order) => order.delivery_address },
    { header: 'ФИО клиента', render: (order) => order.customer_name },
    {
      header: 'ID заказчика',
      render: (order) => <Link to={`/users/user/${order.user_id}`}>{order.user_id}</Link>,
    },
    { header: 'Тел. клиента', render: (order) => order.customer_phone },
    { header: 'Статус заказа', sortField: 'status', render: (order) => order.status_name },
    { header: 'Сумма для клиента', render: (order) => `${formatCost(order.client_order_sum)} ₽` },
    { header: 'Сумма от поставщика', render: (order) => `${formatCost(order.supplier_order_sum)} ₽` },
    { header: 'Оплачено бонусами', render: (order) => `${order.bonus_pay ?? 0} ₽` },
    { header: 'П', render: (order) => <OrderCheckbox order={order} /> },
  ],
};

export const PartsPage = () => <GenericListPage config={config} />;
